/**
 * Google Analytics 4 Event Tracking Utility
 * Provides helper functions to track user interactions and app events
 */

/**
 * Check if gtag is available (GA4 loaded)
 */
const isGAAvailable = () => {
  return typeof window !== 'undefined' && typeof window.gtag === 'function';
};

/**
 * Track a custom event
 * @param {string} eventName - The event name (e.g., 'map_created')
 * @param {object} eventData - Object with event properties
 */
export const trackEvent = (eventName, eventData = {}) => {
  if (!isGAAvailable()) {
    return;
  }

  try {
    window.gtag('event', eventName, {
      timestamp: new Date().toISOString(),
      ...eventData
    });
  } catch (error) {
    console.error('Error tracking GA4 event:', error);
  }
};

/**
 * Track exception/error
 * @param {string} description - Error description
 * @param {boolean} fatal - Whether error is fatal
 */
export const trackException = (description, fatal = false) => {
  if (!isGAAvailable()) return;

  window.gtag('event', 'exception', {
    description,
    fatal
  });
};

// ===== AUTHENTICATION EVENTS =====

export const trackAuthEvents = {
  loginSuccess: (method = 'email') => {
    trackEvent('login', {
      method
    });
  },

  signupSuccess: (method = 'email') => {
    trackEvent('sign_up', {
      method
    });
  },

  logout: () => {
    trackEvent('logout', {});
  },

  emailVerified: () => {
    trackEvent('email_verified', {});
  },

  passwordReset: () => {
    trackEvent('password_reset', {});
  }
};

// ===== ACTIVITY UPLOAD EVENTS =====

export const trackActivityEvents = {
  gpxUploadStart: (fileCount = 1, totalSizeMB = 0) => {
    trackEvent('gpx_upload_start', {
      file_count: fileCount,
      total_size_mb: parseFloat(totalSizeMB.toFixed(2))
    });
  },

  gpxUploadSuccess: (fileCount = 1, totalSizeMB = 0) => {
    trackEvent('gpx_upload_success', {
      file_count: fileCount,
      total_size_mb: parseFloat(totalSizeMB.toFixed(2))
    });
  },

  gpxUploadError: (errorMessage = 'unknown') => {
    trackEvent('gpx_upload_error', {
      error_message: errorMessage
    });
  },

  gpxDelete: () => {
    trackEvent('gpx_delete', {});
  },

  gpxDeleteError: () => {
    trackEvent('gpx_delete_error', {});
  },

  stravaConnectStart: () => {
    trackEvent('strava_connect_start', {});
  },
  stravaRefreshStart: () => {
    trackEvent('strava_refresh_start', {});
  },
  stravaRefreshError: () => {
    trackEvent('strava_refresh_error', {});
  },
  stravaConnectError: () => {
    trackEvent('strava_connect_error', {});
  },

  garminConnectStart: () => {
    trackEvent('garmin_connect_start', {});
  },
  garminRefreshStart: () => {
    trackEvent('garmin_refresh_start', {});
  },
  garminRefreshError: () => {
    trackEvent('garmin_refresh_error', {});
  },
  garminConnectError: () => {
    trackEvent('garmin_connect_error', {});
  },

  activitySourceSelected: (source = 'strava') => {
    trackEvent('activity_source_selected', {
      source // 'strava', 'garmin', 'manual', 'none'
    });
  },

  activityFiltered: (filterType, value) => {
    trackEvent('activity_filtered', {
      filter_type: filterType, // 'date_range', 'activity_type', 'exclude_commutes'
      filter_value: value
    });
  }
};

// ===== MAP CONFIGURATION EVENTS =====

export const trackMapConfigEvents = {
  placeSearched: (placeName = '') => {
    trackEvent('place_searched', {
      place_name: placeName
    });
  },

  placeSelected: (placeName = 'unknown') => {
    trackEvent('place_selected', {
      place_name: placeName
    });
  },

  areaAdjusted: (areaSizeKm2 = 0, minLng = 0, maxLng = 0, minLat = 0, maxLat = 0) => {
    trackEvent('area_adjusted', {
      area_size_km2: parseFloat(areaSizeKm2.toFixed(2)),
      bounds: {
        min_lng: parseFloat(minLng.toFixed(4)),
        max_lng: parseFloat(maxLng.toFixed(4)),
        min_lat: parseFloat(minLat.toFixed(4)),
        max_lat: parseFloat(maxLat.toFixed(4))
      }
    });
  },

  formatSelected: (format = 'A4', orientation = 'portrait') => {
    trackEvent('format_selected', {
      format, // 'A4', 'A3', 'A2', etc.
      orientation // 'portrait' or 'landscape'
    });
  },

  topographyToggled: (enabled = false) => {
    trackEvent('topography_toggled', {
      enabled
    });
  },

  styleSelected: (styleName = 'default') => {
    trackEvent('map_style_selected', {
      style_name: styleName
    });
  },

  colorCustomized: (setting = '', value = '') => {
    trackEvent('color_customized', {
      setting, // 'water', 'ocean', 'background', etc.
      value
    });
  },

  frameToggled: (enabled = false) => {
    trackEvent('frame_toggled', {
      enabled
    });
  },

  frameCustomized: (frameType = '') => {
    trackEvent('frame_customized', {
      frame_type: frameType
    });
  }
};

// ===== MAP GENERATION EVENTS =====

export const trackGenerationEvents = {
  generateSuccess: (generationTimeSeconds = 0, requestData = {} ) => {
    trackEvent('map_generation_success', {
      format: requestData.selectedFormat.value || 'unknown',
      place: requestData.placeName || 'unknown',
      activity_source: requestData.activitySource || 'none',
      generation_time_seconds: Math.round(generationTimeSeconds),
      frame: requestData.frame || false,
      topgraphy: requestData.topography || true,
      filtered_activites: Object.keys(requestData.filteredActivities).filter(activity => requestData.filteredActivities[activity]) || [],
      km2: requestData.settings.km2 || 0,
      allDates: requestData.allDates || true,
      activities_active: requestData.settings.activities.active,
      buildings_active: requestData.settings.buildings.active,
      forests_active: requestData.settings.forests.active,
      naturalareas_active: requestData.settings.naturalareas.active,
      water_active: requestData.settings.water.active,
      oceans_active: requestData.settings.oceans.active,
      streets_active: requestData.settings.streets.active,
      streets_details: requestData.settings.streets.details,
      land_active: requestData.settings.land.active,
    });
  },

  generateError: (generationTimeSeconds = 0, requestData = {}) => {
    trackEvent('map_generation_error', {
      format: requestData.selectedFormat.value || 'unknown',
      place: requestData.placeName || 'unknown',
      activity_source: requestData.activitySource || 'none',
      generation_time_seconds: Math.round(generationTimeSeconds),
      frame: requestData.frame || false,
      topgraphy: requestData.topography || true,
      filtered_activites: Object.keys(requestData.filteredActivities).filter(activity => requestData.filteredActivities[activity]) || [],
      km2: requestData.settings.km2 || 0,
      allDates: requestData.allDates || true,
      activities_active: requestData.settings.activities.active,
      buildings_active: requestData.settings.buildings.active,
      forests_active: requestData.settings.forests.active,
      naturalareas_active: requestData.settings.naturalareas.active,
      water_active: requestData.settings.water.active,
      oceans_active: requestData.settings.oceans.active,
      streets_active: requestData.settings.streets.active,
      streets_details: requestData.settings.streets.details,
      land_active: requestData.settings.land.active,
    });
  },
  
  previewGenerated: (styleCount = 0) => {
    trackEvent('preview_generated', {
      style_count: styleCount
    });
  },

  cacheHit: (cacheKey = '') => {
    trackEvent('cache_hit', {
      cache_key: cacheKey
    });
  }
};

// ===== PURCHASE & CHECKOUT EVENTS =====

export const trackPurchaseEvents = {
  voucherApplied: (voucherCode = '', discountPercent = 0) => {
    trackEvent('voucher_applied', {
      voucher_code: voucherCode,
      discount_percent: discountPercent
    });
  },

  checkoutInitiated: (size = '', width = 0, height = 0) => {
    trackEvent('begin_checkout', {
      size, // 'XXS', 'XS', 'S', 'M', 'L', 'XL'
      width_cm: parseFloat(width.toFixed(2)),
      height_cm: parseFloat(height.toFixed(2)),
    });
  },

  checkoutAbandoned: () => {
    trackEvent('checkout_abandoned', {});
  },

  paymentSuccess: (size = '', needsRegeneration = false) => {
    trackEvent('payment_success', {
      size,
      needs_regeneration: needsRegeneration,
    });
  },

  paymentSuccessError: (size = '', needsRegeneration = false, errorMessage = 'unknown') => {
    trackEvent('payment_success_error', {
      size,
      needs_regeneration: needsRegeneration,
      error_message: errorMessage
    });
  },

  paymentFailed: (errorMessage = 'unknown') => {
    trackEvent('payment_failed', {
      error_message: errorMessage
    });
  },
  checkoutFailed: () => {
    trackEvent('stripe_failed', {});
  }
};

// ===== HISTORY & DOWNLOAD EVENTS =====

export const trackHistoryEvents = {
  historyViewed: () => {
    trackEvent('history_viewed', {});
  },

  imageLoadedInGallery: (imageIndex = 0, totalImages = 0) => {
    trackEvent('image_loaded_in_gallery', {
      image_index: imageIndex,
      total_images: totalImages
    });
  },

  paginationLoaded: (page = 1, itemCount = 0) => {
    trackEvent('pagination_loaded', {
      page,
      item_count: itemCount
    });
  },

  imageDownloaded: (imageId = '', size = 'unknown') => {
    trackEvent('image_downloaded', {
      image_id: imageId,
      size
    });
  },

  imageDeleted: (imageId = '') => {
    trackEvent('image_deleted', {
      image_id: imageId
    });
  },

  imageShared: (platform = 'unknown') => {
    trackEvent('image_shared', {
      platform
    });
  }
};

// ===== PLAYGROUND (DEMO) EVENTS =====

export const trackPlaygroundEvents = {
  playgroundEntered: () => {
    trackEvent('playground_entered', {});
  },

  playgroundMapGenerated: (placeName = 'unknown') => {
    trackEvent('playground_map_generated', {placeName});
  },

  playgroundMapError: (placeName = 'unknown') => {
    trackEvent('playground_map_error', {placeName});
  },

  playgroundConvertedToSignup: () => {
    trackEvent('playground_converted_to_signup', {});
  }
};

// ===== USER ENGAGEMENT EVENTS =====

export const trackEngagementEvents = {
  pageViewed: (pageName = '') => {
    trackEvent('page_view', {
      page_name: pageName
    });
  },

  contactFormSubmitted: () => {
    trackEvent('contact_form_submitted', {});
  },

  faqViewed: (topic = '') => {
    trackEvent('faq_viewed', {
      topic
    });
  },

  documentationViewed: (docTitle = '') => {
    trackEvent('documentation_viewed', {
      doc_title: docTitle
    });
  },

  refundViewed: () => {
    trackEvent('refund_viewed', {});
  },
  privacyViewed: () => {
    trackEvent('privacy_viewed', {});
  },
  termsViewed: () => {
    trackEvent('terms_viewed', {});
  },
  contactViewed: () => {
    trackEvent('contact_viewed', {});
  },
  impressumViewed: () => {
    trackEvent('impressum_viewed', {});
  },
  aboutViewed: () => {
    trackEvent('about_viewed', {});
  },

  featureTourStarted: (featureName = '') => {
    trackEvent('feature_tour_started', {
      feature_name: featureName
    });
  },

  feedbackSubmitted: (rating = 0) => {
    trackEvent('feedback_submitted', {
      rating
    });
  }
};

export default {
  trackEvent,
  trackException,
  trackAuthEvents,
  trackActivityEvents,
  trackMapConfigEvents,
  trackGenerationEvents,
  trackPurchaseEvents,
  trackHistoryEvents,
  trackPlaygroundEvents,
  trackEngagementEvents
};
